import React, { createContext, useState, useEffect } from 'react';
import { College } from '../types/college';

interface CollegeSelectionContextType {
  selectedColleges: College[];
  toggleCollegeSelection: (college: College) => void;
  isCollegeSelected: (id: string) => boolean;
  clearSelections: () => void;
}

export const CollegeSelectionContext = createContext<CollegeSelectionContextType>({
  selectedColleges: [],
  toggleCollegeSelection: () => {},
  isCollegeSelected: () => false,
  clearSelections: () => {},
});

export const CollegeSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedColleges, setSelectedColleges] = useState<College[]>([]);
  
  // Load selected colleges from local storage on mount
  useEffect(() => {
    const savedSelections = localStorage.getItem('selectedColleges');
    
    if (savedSelections) {
      try {
        setSelectedColleges(JSON.parse(savedSelections));
      } catch (error) {
        console.error('Error loading saved college selections:', error);
      }
    }
  }, []);
  
  // Save selected colleges to local storage whenever they change
  useEffect(() => {
    localStorage.setItem('selectedColleges', JSON.stringify(selectedColleges));
  }, [selectedColleges]);
  
  // Toggle college selection, respecting the maximum limit of 10
  const toggleCollegeSelection = (college: College) => {
    setSelectedColleges(prev => {
      const isSelected = prev.some(c => c.id === college.id);
      
      if (isSelected) {
        // Remove college if already selected
        return prev.filter(c => c.id !== college.id);
      } else {
        // Add college if not at maximum
        if (prev.length < 10) {
          return [...prev, college];
        }
        return prev;
      }
    });
  };
  
  // Check if a college is selected
  const isCollegeSelected = (id: string): boolean => {
    return selectedColleges.some(college => college.id === id);
  };
  
  // Clear all selections
  const clearSelections = () => {
    setSelectedColleges([]);
  };
  
  return (
    <CollegeSelectionContext.Provider
      value={{
        selectedColleges,
        toggleCollegeSelection,
        isCollegeSelected,
        clearSelections,
      }}
    >
      {children}
    </CollegeSelectionContext.Provider>
  );
};